import { Box } from "@mui/material";
import { DotLegalHeader, DotLegalPdfBackPage } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import PdfReportFrontPage from "@common/PdfReportFrontPage";
import { useSetPdfPageSize } from "@common/pdfPageSizeHelper";
import { RiskAreaMatrix } from "../../../riskSettings/riskArea/riskAreaDialog/riskAreaMatrix/RiskAreaMatrix";
import RiskGovernanceStatRow from "../RiskGovernanceStatRow";
import RiskDistributionHistoryChart from "../riskDistributionHistoryChart/RiskDistributionHistoryChart";
import RiskGovernanceViewBanner from "../RiskGovernanceViewBanner";
import { useRiskGovernanceOverviewPdf } from "./RiskGovernanceOverviewPdf.hooks";

// Stands in for the paper shadow the tiles lose in print (same colour), so each tile keeps a definite edge
// against the white page instead of reading as a floating wave.
const printTileBorder = "#E7E7E7";

function RiskGovernanceOverviewPdf() {
    const { translateString } = useTranslation();
    const {
        isLoading,
        hasData,
        scale,
        appetite,
        assessedCount,
        assessedTotalLabel,
        controlCoverageLabel,
        notAssessedCount,
        assessedProgress,
        overAppetiteCount,
        untreatedOverAppetiteCount,
        linkedRiskOverAppetiteCount,
        overduePlans,
        plansWithoutLinkedControls,
        controlCoverage,
        exposureTile,
        showExposureTile,
        consequenceLevels,
        probabilityLevels,
        riskLevelIndicators,
        riskLevels,
        matrix,
        cellCounts,
        distributionHistory,
        riskAreaName,
        asOf,
        afterMitigation,
    } = useRiskGovernanceOverviewPdf();
    useSetPdfPageSize("landscape");

    if (isLoading) {
        return null;
    }

    return (
        <Box>
            <PdfReportFrontPage title={riskAreaName || translateString("riskGovernanceOverviewFileName")} orientation="landscape" />
            {/* Puppeteer rasterises DotLegalPaper's #E7E7E7 shadow as a flat block that fills the tiles' grid gaps
                instead of a blur, and drops it altogether for some tiles, so the report reads as "grey behind some
                boxes but not others". The printed report carries no paper shadows at all. */}
            <Box ml={2} mr={2} sx={{ "& .MuiPaper-root": { boxShadow: "none" } }}>
                {/* padding-top, not margin-top: this section opens the first page after the cover, and a margin
                    against a page break is dropped while padding survives (the PDF has no page margin). */}
                <Box sx={{ pt: 3, "& .MuiPaper-root": { border: `1px solid ${printTileBorder}` } }} className="avoid-page-break">
                    <RiskGovernanceStatRow
                        isLoading={false}
                        isAsOf={asOf !== null}
                        afterMitigation={afterMitigation}
                        showExposureTile={showExposureTile}
                        assessedCount={assessedCount}
                        assessedTotalLabel={assessedTotalLabel}
                        controlCoverageLabel={controlCoverageLabel}
                        notAssessedCount={notAssessedCount}
                        assessedProgress={assessedProgress}
                        appetite={appetite}
                        overAppetiteCount={overAppetiteCount}
                        untreatedOverAppetiteCount={untreatedOverAppetiteCount}
                        linkedRiskOverAppetiteCount={linkedRiskOverAppetiteCount}
                        overduePlans={overduePlans}
                        plansWithoutLinkedControls={plansWithoutLinkedControls}
                        controlCoverage={controlCoverage}
                        exposureTile={exposureTile}
                    />
                </Box>

                {hasData && (
                    // padding-top, not margin-top: when this section breaks onto its own page the matrix would
                    // otherwise sit flush against the paper's top edge (the PDF is generated with no page margin).
                    // A top margin is dropped at a page break, but padding is preserved.
                    <Box sx={{ pt: 3 }} className="avoid-page-break">
                        <RiskGovernanceViewBanner isAsOf={asOf !== null} asOf={asOf} afterMitigation={afterMitigation} />
                        <DotLegalHeader headerStyle="small">{translateString("riskMatrix")}</DotLegalHeader>
                        <RiskAreaMatrix
                            readOnly
                            scale={scale}
                            consequenceLevels={consequenceLevels}
                            probabilityLevels={probabilityLevels}
                            riskLevelIndicators={riskLevelIndicators}
                            riskLevels={riskLevels}
                            matrix={matrix}
                            cellBadges={cellCounts}
                            selectedConsequence={null}
                            selectedProbability={null}
                            onCellClick={() => {}}
                        />
                    </Box>
                )}

                {/* padding-top for the same reason as the section above: this one forces its own page, so a
                    margin-top would be dropped and the header would sit flush against the paper's top edge. */}
                <Box sx={{ pt: 3 }} className="page-break-before">
                    <DotLegalHeader headerStyle="small">{translateString("riskGovernanceDistributionHistory")}</DotLegalHeader>
                    {/* ResponsiveBar needs a definite width and print has no sized parent; the chart brings its
                        own height (250mm ≈ the landscape page width inside the report's side margins). */}
                    <Box sx={{ width: "250mm" }}>
                        <RiskDistributionHistoryChart
                            isPdf
                            isLoading={false}
                            history={distributionHistory}
                            riskLevels={riskLevels}
                            riskLevelIndicators={riskLevelIndicators}
                            asOf={asOf}
                        />
                    </Box>
                </Box>
            </Box>
            <Box className="page-break-before" sx={{ "& > div": { height: "210mm" } }}>
                <DotLegalPdfBackPage />
            </Box>
        </Box>
    );
}

export default RiskGovernanceOverviewPdf;
